import { useEffect, useMemo, useState } from "react";
import { BadgePercent, BookOpen, Download, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

import { eur, perWp, pct } from "@/lib/price";
const WpSub = ({ v, p }) => (perWp(v, p) ? <div className="text-[11px] font-normal text-muted-foreground">{perWp(v, p)}</div> : null);
const LIVELLI = [[0, "Nessuno", "Prezzo all'installatore"], [1, "Livello 1", "1/3 verso il minimo"], [2, "Livello 2", "2/3 verso il minimo"], [3, "Livello 3", "Prezzo minimo"]];

const downloadPdf = async (url, setBusy) => {
  setBusy(true);
  try {
    const r = await api.get(url, { responseType: "blob", timeout: 120000 });
    const u = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = u; a.download = "listino_cliente.pdf"; document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(u);
  } catch { toast.error("Download PDF non riuscito"); }
  setBusy(false);
};

function ListinoClienteDialog({ partnerId, open, onOpenChange }) {
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setD(null);
    api.get(`/partners/${partnerId}/listino-extra`).then((r) => setD(r.data)).catch(() => toast.error("Listino non disponibile"));
  }, [open, partnerId]);
  const list = useMemo(() => (d?.products || []).filter((p) => (!cat || p.tipologia === cat) && (!q || `${p.codice} ${p.descrizione}`.toLowerCase().includes(q.toLowerCase()))), [d, q, cat]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] max-w-4xl overflow-y-auto" data-testid="listino-cliente-dialog" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center justify-between gap-3 pr-6">
            <span className="flex items-center gap-2"><BookOpen className="h-5 w-5" />Listino cliente{d ? ` · ${d.livello ? `Extra sconto livello ${d.livello}` : "nessun extra sconto"}` : ""}</span>
            <Button size="sm" variant="outline" onClick={() => downloadPdf(`/partners/${partnerId}/listino-extra/pdf`, setBusy)} disabled={busy} data-testid="listino-cliente-pdf">
              {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Download className="mr-1 h-4 w-4" />}PDF
            </Button>
          </DialogTitle>
        </DialogHeader>
        {!d ? <Loader2 className="h-5 w-5 animate-spin" /> : (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              <div className="relative min-w-[220px] flex-1"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca codice o descrizione…" className="pl-8" data-testid="listino-cliente-search" /></div>
              <select value={cat} onChange={(e) => setCat(e.target.value)} className="h-9 rounded-md border border-border bg-background px-2 text-sm" data-testid="listino-cliente-cat">
                <option value="">Tutte le categorie</option>{d.tipologie.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <table className="w-full text-sm" data-testid="listino-cliente-table">
              <thead><tr className="border-b border-border text-left text-xs uppercase text-muted-foreground"><th className="py-2">Codice</th><th>Descrizione</th><th className="text-right">Prezzo di listino</th><th className="text-right">Sconto</th><th className="text-right">Prezzo riservato</th></tr></thead>
              <tbody>{list.map((p) => (
                <tr key={p.id} className="border-b border-border/50" data-testid={`listino-cliente-row-${p.id}`}>
                  <td className="py-1.5 pr-2 font-semibold">{p.codice}</td>
                  <td className="max-w-[320px] truncate pr-2 text-xs text-muted-foreground" title={p.descrizione}>{p.tipologia} · {p.descrizione}</td>
                  <td className="text-right font-mono"><span className={p.sconto_pct ? "text-muted-foreground line-through" : ""}>{eur(p.prezzo_listino)}</span><WpSub v={p.prezzo_listino} p={p} /></td>
                  <td className="text-right font-mono text-xs" data-testid={`listino-cliente-sconto-${p.id}`}>{pct(p.sconto_pct)}</td>
                  <td className="text-right font-mono font-bold">{eur(p.prezzo)}<WpSub v={p.prezzo} p={p} /></td>
                </tr>
              ))}</tbody>
            </table>
            {!list.length && <p className="py-4 text-center text-sm text-muted-foreground">Nessun articolo trovato.</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function ExtraScontoCard({ partner, onChanged }) {
  const [lv, setLv] = useState(partner.extra_sconto || 0);
  const [saving, setSaving] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => setLv(partner.extra_sconto || 0), [partner.extra_sconto]);
  const save = async (v) => {
    setSaving(v);
    try { await api.put(`/partners/${partner.id}/extra-sconto`, { livello: v }); setLv(v); toast.success(v ? `Extra sconto livello ${v} applicato` : "Extra sconto rimosso"); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Salvataggio non riuscito"); }
    setSaving(null);
  };
  return (
    <Card className="border-2 border-amber-400/70 bg-amber-400/5 p-6" data-testid="extra-sconto-card">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 font-heading text-lg font-bold tracking-tight"><BadgePercent className="h-5 w-5 text-amber-500" />Extra sconto</h2>
          <p className="text-xs text-muted-foreground">Tre step uguali dal prezzo all'installatore (prezzo target) al prezzo minimo installatore. Il cliente vede sempre il prezzo di listino e lo sconto calcolato sul listino. Si applica a tutto il listino, anche su Ai VESTA.</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="extra-sconto-listino-btn"><BookOpen className="mr-2 h-4 w-4" />Vedi listino cliente</Button>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="extra-sconto-levels">
        {LIVELLI.map(([v, l, sub]) => (
          <button key={v} type="button" onClick={() => v !== lv && save(v)} disabled={saving != null} data-testid={`extra-sconto-level-${v}`}
            className={`rounded-lg border-2 p-3 text-left transition-colors ${lv === v ? "border-amber-500 bg-amber-500 text-black" : "border-border hover:border-amber-400"}`}>
            <div className="flex items-center gap-1.5 text-sm font-bold">{saving === v && <Loader2 className="h-3.5 w-3.5 animate-spin" />}{l}</div>
            <div className={`text-[11px] ${lv === v ? "text-black/70" : "text-muted-foreground"}`}>{sub}</div>
          </button>
        ))}
      </div>
      {partner.extra_sconto_at && <p className="mt-2 text-[11px] text-muted-foreground" data-testid="extra-sconto-updated">Aggiornato il {new Date(partner.extra_sconto_at).toLocaleString("it-IT")}{partner.extra_sconto_by ? ` · ${partner.extra_sconto_by}` : ""}</p>}
      <ListinoClienteDialog partnerId={partner.id} open={open} onOpenChange={setOpen} />
    </Card>
  );
}
